import { ApolloClient, HttpLink, InMemoryCache, from, split, NormalizedCacheObject, ApolloLink } from '@apollo/client'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import * as Sentry from '@sentry/nextjs'
import ApolloLinkTimeout from 'apollo-link-timeout'
import type { IncomingMessage } from 'http'
import { useMemo } from 'react'
import { getAuthIdToken, getSessionCookieToken } from 'api/authCookies'
import { typePolicies } from 'api/typePolicies'
import { config } from 'utils/config'
import { getSegmentTrackingId } from './segmentCookie'

let apolloClientInstance: ApolloClient<NormalizedCacheObject>

export type ApolloClientOptions = {
  req?: IncomingMessage
  authorization?: string
  sessionAuthorization?: string
  anonymousId?: string
}

const createApolloClient = (options: ApolloClientOptions) => {
  const cache = new InMemoryCache({
    typePolicies
  })

  const httpLink = new HttpLink({
    uri: config.graphqlUri,
    fetch
  })

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (process.env.NODE_ENV === 'development') {
      if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) =>
          // eslint-disable-next-line no-console
          console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
        )

      // eslint-disable-next-line no-console
      if (networkError) console.warn(`[Network error]: ${networkError}`)
    }
  })

  const timeoutLink = new ApolloLinkTimeout(60 * 1000).concat(httpLink)

  const batchHttpLink = new BatchHttpLink({
    uri: config.graphqlUri,
    fetch,
    headers: { batch: 'true' }
  })

  const retryLink = new RetryLink()

  const sentryBreadcrumbLink = new ApolloLink((operation, forward) => {
    Sentry.addBreadcrumb({
      category: 'apollo',
      message: operation.operationName,
      data: operation.variables
    })

    return forward(operation)
  })

  const authMiddleware = setContext(async (_, { headers }) => {
    try {
      const appHeaders: any = { ...headers }

      if (typeof window === 'undefined') {
        appHeaders.authorization = options.authorization
        appHeaders.sessionAuthorization = options.sessionAuthorization
        appHeaders.anonymousId = options.anonymousId
      } else {
        appHeaders.authorization = await getAuthIdToken(options.req)
        appHeaders.sessionAuthorization = getSessionCookieToken(options.req)
        appHeaders.anonymousId = getSegmentTrackingId(options.req)
      }

      if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
        window.authHeader = appHeaders.authorization
      }

      return {
        headers: appHeaders
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err)
      return headers
    }
  })

  const links = [
    authMiddleware,
    sentryBreadcrumbLink,
    retryLink,
    errorLink,
    split((operation) => operation.getContext().batch === true, batchHttpLink, timeoutLink)
  ]

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    cache,
    link: from(links),
    name: 'TT-frontend',
    version: `1.0.${config.apiEnv}`,
    defaultOptions: {
      watchQuery: {
        refetchWritePolicy: 'overwrite'
      }
    }
  })
}

interface InitializationOptions extends ApolloClientOptions {
  initialState?: NormalizedCacheObject | null
}
export const initializeApollo = ({ initialState = null, req, authorization, sessionAuthorization }: InitializationOptions = {}) => {
  const client = apolloClientInstance ?? createApolloClient({ req, authorization, sessionAuthorization })

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    client.cache.restore(initialState)
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') {
    return client
  }

  // Create the Apollo Client once in the client
  if (!apolloClientInstance) {
    apolloClientInstance = client
  }

  return client
}

export const useApollo = (options: InitializationOptions) => {
  return useMemo(() => initializeApollo(options), [options])
}
